import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, setWorkingBranch } from "./api";
import { Link, useRouter } from "./router";
import { useSession } from "./session";
import { Notice } from "./components";

export interface TenantContext {
  organisation: { name: string; type: string };
  branches: { id: string; code: string; name: string; city: string | null }[];
  departmentCount: number; permissions: string[]; resources: string[];
}
const Ctx = createContext<{ ctx: TenantContext; branchId: string; can: (p: string) => boolean }>(null as never);
export const useTenant = () => useContext(Ctx);

/** Signed-in layout: organisation bar, branch picker, navigation by permission. */
export function Shell({ children }: { children: ReactNode }) {
  const { me, ready, signOut } = useSession();
  const { navigate, path } = useRouter();
  const [ctx, setCtx] = useState<TenantContext | null>(null);
  const [branchId, setBranchId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    if (!me) { navigate("/login", true); return; }
    if (!me.tenantId) { navigate("/select-organisation", true); return; }
    api.authed("/api/v1/tenant/context").then((c: TenantContext) => {
      setCtx(c);
      const only = c.branches.length === 1 ? c.branches[0]!.id : "";
      setBranchId(only); setWorkingBranch(only || null);
    }).catch((e) => setError(e.message));
  }, [ready, me, navigate]);

  if (!ready || !me) return null;
  const can = (p: string) => !!ctx?.permissions.includes(p);
  const nav = [
    { to: "/app", label: "Home", show: true },
    { to: "/app/queue", label: "Queue", show: can("queue:view") || can("queue:edit") },
    { to: "/app/appointments", label: "Appointments", show: can("appointment:view") },
    { to: "/app/patients", label: "Patients", show: can("patient:view") },
    { to: "/app/lab", label: "Laboratory", show: can("lab_sample:edit") || can("lab_request:create") || can("lab_catalog:edit") },
    { to: "/app/billing", label: "Billing", show: can("invoice:view") },
    { to: "/app/website", label: "Website", show: can("website:view") },
    { to: "/app/doctors", label: "Doctors", show: can("schedule:view") },
    { to: "/app/messages", label: "Messages", show: can("communication:view") },
    { to: "/app/duplicates", label: "Duplicate records", show: can("patient_merge:create") || can("patient_merge:approve") },
  ].filter((n) => n.show);
  const active = (to: string) => (to === "/app" ? path === "/app" : path.startsWith(to));

  return (
    <div className="shell">
      <header className="shell-top">
        <span className="org-name">{ctx?.organisation.name ?? ""}</span>
        <div className="shell-user">
          {ctx && ctx.branches.length > 1 && (
            <label className="branch-pick">
              <span className="sr-only">Working branch</span>
              <select value={branchId} onChange={(e) => { setBranchId(e.target.value); setWorkingBranch(e.target.value || null); }}>
                <option value="">Choose branch…</option>
                {ctx.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
          )}
          <Link to="/app/profile" className="header-user">{me.user.fullName}</Link>
          {me.memberships.length > 1 && <button className="link-button" onClick={() => navigate("/select-organisation")}>Switch organisation</button>}
          <button className="btn btn-quiet" onClick={async () => { await signOut(); navigate("/login", true); }}>Sign out</button>
        </div>
      </header>
      <div className="shell-body">
        <nav className="side-nav" aria-label="Main">
          <ul>{nav.map((n) => <li key={n.to}><Link to={n.to} className={active(n.to) ? "active" : ""}>{n.label}</Link></li>)}</ul>
        </nav>
        <main className="shell-main">
          {error && <Notice tone="error">{error}</Notice>}
          {ctx && <Ctx.Provider value={{ ctx, branchId, can }}>{children}</Ctx.Provider>}
        </main>
      </div>
    </div>
  );
}

export function NeedBranch({ children }: { children: ReactNode }) {
  const { branchId } = useTenant();
  if (!branchId) return <Notice tone="info">Choose the branch you are working at, at the top of the page.</Notice>;
  return <>{children}</>;
}
